/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import {
  FiArrowRight,
  FiBriefcase,
  FiCalendar,
  FiMapPin,
  FiMoon,
  FiZap,
} from 'react-icons/fi';
import PackageImage from '../common/PackageImage';
import {
  daysNightsText,
  formatIsoDate,
  formatPrice,
  packageCurrency,
  packageDays,
  packageFromPrice,
  packageImageUrl,
  packageNights,
  packagePlace,
  packageSupplier,
} from '../../utils/packageUtils';
import './PackageCard.scss';

/**
 * One package in the results. The price is the lead fare per person
 * ("from"); the exact total is quoted once a departure, joining city and
 * room sharing are picked on the package page.
 */
function PackageCard({ pkg, cheapest, onOpen }) {
  const price = packageFromPrice(pkg);
  const days = packageDays(pkg);
  const nights = packageNights(pkg);
  const place = packagePlace(pkg);
  const supplier = packageSupplier(pkg);
  const upcoming = Number(pkg.upcomingDepartures) || 0;
  const open = () => onOpen(pkg);

  return (
    <article className="pkg-card">
      <button
        type="button"
        className="pkg-card__media"
        onClick={open}
        aria-label={`View ${pkg.name || 'package'}`}
      >
        <PackageImage
          src={packageImageUrl(pkg.coverImage)}
          alt={pkg.name || ''}
        />
        {days != null && (
          <span className="pkg-card__duration">
            <FiMoon size={12} />
            {daysNightsText(days, nights)}
          </span>
        )}
      </button>

      <div className="pkg-card__body">
        <div className="pkg-card__info">
          <div className="pkg-card__top">
            {supplier && (
              <span className="pkg-card__supplier">
                <FiBriefcase size={12} />
                {supplier}
              </span>
            )}
            {cheapest && (
              <span className="pkg-chip pkg-chip--accent">
                <FiZap size={12} /> Best price
              </span>
            )}
          </div>

          <h3 className="pkg-card__title">
            <button type="button" onClick={open}>
              {pkg.name || '—'}
            </button>
          </h3>

          <div className="pkg-card__meta">
            {place && (
              <span>
                <FiMapPin size={13} /> {place}
              </span>
            )}
            {pkg.nextDeparture ? (
              <span>
                <FiCalendar size={13} /> Next departure{' '}
                <strong>{formatIsoDate(pkg.nextDeparture)}</strong>
              </span>
            ) : (
              <span className="pkg-card__muted">
                <FiCalendar size={13} /> No upcoming departure listed
              </span>
            )}
            {upcoming > 1 && (
              <span className="pkg-chip pkg-chip--primary">
                +{upcoming - 1} more date{upcoming - 1 === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {pkg.summary && (
            <p className="pkg-card__summary">{pkg.summary.trim()}</p>
          )}
        </div>

        <div className="pkg-card__price">
          <small>Starting from</small>
          <strong>
            {price != null ? formatPrice(price, packageCurrency(pkg)) : '—'}
          </strong>
          <small>per person</small>
          <button type="button" className="pkg-card__cta" onClick={open}>
            View Details <FiArrowRight />
          </button>
        </div>
      </div>
    </article>
  );
}

PackageCard.propTypes = {
  pkg: PropTypes.object.isRequired,
  cheapest: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
};

PackageCard.defaultProps = {
  cheapest: false,
};

export default PackageCard;
